import { useMemo } from "react";
import { computeDailyView } from "@hicya/habits-shared/engine";

import { useHabits } from "@/context/habits-context";

import type { TimeOfDay } from "@hicya/habits-shared/db";
import type {
  DailyViewData,
  HabitDisplay,
  LayoutMode,
  TimeBlock,
} from "@hicya/habits-shared/engine";

// Re-export shared types for consumers
export type { DailyViewData, HabitDisplay, LayoutMode, TimeBlock };

export const FILTER_UNCATEGORIZED = "uncategorized";

const BLOCK_ORDER: TimeOfDay[] = ["morning", "afternoon", "evening", "anytime"];
const BLOCK_LABELS: Record<TimeOfDay, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  anytime: "Anytime",
};

export function useDailyView(categoryFilter?: string | null): DailyViewData {
  const { state } = useHabits();
  const { habits, completions, categories, settings, today } = state;

  return useMemo(() => {
    const base = computeDailyView({ habits, completions, categories, settings, today });

    // No filter — return base computation
    if (categoryFilter === undefined) {
      return base;
    }

    // Apply category filter to blocks
    const filteredHabits =
      categoryFilter === FILTER_UNCATEGORIZED
        ? base.allHabits.filter((hd) => hd.habit.category_id === null)
        : base.allHabits.filter((hd) => hd.habit.category_id === categoryFilter);

    const blockMap = new Map<TimeOfDay, HabitDisplay[]>();
    for (const block of BLOCK_ORDER) {
      blockMap.set(block, []);
    }
    for (const hd of filteredHabits) {
      const list = blockMap.get(hd.habit.time_of_day);
      if (list) {
        list.push(hd);
      }
    }

    const blocks: TimeBlock[] = BLOCK_ORDER.map((name) => {
      const blockHabits = blockMap.get(name) ?? [];
      return {
        name,
        label: BLOCK_LABELS[name],
        habits: blockHabits,
        completed: blockHabits.filter((h) => h.dayState === "done" || h.dayState === "skipped")
          .length,
        total: blockHabits.length,
      };
    }).filter((b) => b.total > 0);

    return {
      ...base,
      blocks,
    };
  }, [habits, completions, categories, settings, today, categoryFilter]);
}
